import * as paper from 'paper';
import {Point} from "paper";

export class SimplePoint {
    x: number;
    y: number;

    constructor(xLoc: number, yLoc: number) {
        if (xLoc == null) {
            this.x = 0;
            this.y = 0;
        } else {
            this.x = xLoc;
            this.y = yLoc;
        }
    }
}

export class SimpleLine {
    point1: paper.Point;
    point2: paper.Point;

    constructor(p1: paper.Point, p2: paper.Point) {
        this.point1 = new paper.Point(p1);
        this.point2 = new paper.Point(p2);
    }
}

export class SimpleSize {
    width: number;
    height: number;

    constructor(myWidth: number, myHeight: number) {
        this.width = myWidth;
        this.height = myHeight;
    }
}

export class SimpleShape {
    line1: SimpleLine;
    line2: SimpleLine;
    line3: SimpleLine;
    line4: SimpleLine;

    constructor(l1: SimpleLine, l2: SimpleLine, l3: SimpleLine, l4: SimpleLine) {
        this.line1 = l1;
        this.line2 = l2;
        this.line3 = l3;
        this.line4 = l4;
    }
}

export class SimpleSegment {
    point: number[] | undefined;
    handleIn: number[] | undefined;
    handleOut: number[] | undefined;

    constructor(segment: any) {
        this.point = undefined;
        this.handleIn = undefined;
        this.handleOut = undefined;
        if (segment.point) {
            this.point = [segment.point.x, segment.point.y];
        }
        if (segment.handleIn) this.handleIn = [segment.handleIn.x, segment.handleIn.y];
        if (segment.handleOut) this.handleOut = [segment.handleOut.x, segment.handleOut.y];
    }
}

export class LongSimpleSegment {
    point: number[] | undefined;
    handleIn: number[] | undefined;
    handleOut: number[] | undefined;

    constructor(point: any, handleIn: any, handleOut: any) {
        this.point = undefined;
        this.handleIn = undefined;
        this.handleOut = undefined;

        if (point) {
            this.point = [point.x, point.y];
        }
        if (handleIn)
            this.handleIn = [handleIn.x, handleIn.y];
        if (handleOut)
            this.handleOut = [handleOut.x, handleOut.y];
    }
}

export class MathFunctions {

    static multiplyVectorByScalar(v: paper.Point, s: number): paper.Point {
        return new Point(v.x * s, v.y * s);
    }

    static addVectors(v1: paper.Point, v2: paper.Point): paper.Point {
        return new Point(v1.x + v2.x, v1.y + v2.y);
    }

    static roundVector(v: paper.Point): paper.Point {
        return new paper.Point(Math.round(v.x), Math.round(v.y));
    }

    static getVectorBetweenPoints(p1: paper.Point, p2: paper.Point): paper.Point {
        return new Point(p2.x - p1.x, p2.y - p1.y);
    }

    static getDistanceBetween(p1: paper.Point, p2: paper.Point): number {
        return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    }

    static getMidpoint(p1: paper.Point, p2: paper.Point): paper.Point {
        //2d:
        if (!p2) {
            return p1;
        }
        if (!p1) {
            return p2;
        }
        return new Point((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
    }

    static isHorizontallyAligned(p1: paper.Point, p2: paper.Point, round?: boolean): boolean {
        var y1 = p1.y;
        var y2 = p2.y;
        if (round) {
            return Math.round(y1) === Math.round(y2);
        }
        return (y1 === y2);
    }

    static isVerticallyAligned(p1: paper.Point, p2: paper.Point, round?: boolean): boolean {
        var x1 = p1.x;
        var x2 = p2.x;
        if (round) {
            return Math.round(x1) === Math.round(x2);
        }
        return (x1 === x2);
    }

    static isHorizontallyOrVerticallyAligned(p1: paper.Point, p2: paper.Point): boolean {
        return (MathFunctions.isHorizontallyAligned(p1, p2) || MathFunctions.isVerticallyAligned(p1, p2));
    }

    static getUnitNormal(p1: any, p2: any): paper.Point {
        if (MathFunctions.isVerticallyAligned(p1, p2)) {
            return new Point(MathFunctions.normalizeOneDimensional(p2.y - p1.y), 0);
        }
        if (MathFunctions.isHorizontallyAligned(p1, p2)) {
            return new Point(0, MathFunctions.normalizeOneDimensional(p2.x - p1.x));
        }
        var slope = -((p2.x - p1.x) / (p2.y - p1.y));
        return MathFunctions.normalizeTwoDimensionalSlop(slope);
    }

    static normalizeOneDimensional(length: number): number {
        if (length > 0) {
            return 1;
        }
        if (length < 0) {
            return -1;
        }
        return length;
    }

    static normalizeTwoDimensionalSlop(slop: number): paper.Point {
        return MathFunctions.normalizeTwoDimensional(new Point(1, slop));
    }

    static normalizeTwoDimensional(vector: paper.Point): paper.Point {
        var length = MathFunctions.getVectorLength(vector);
        if (length === 0) {
            return new Point(0, 0);
        }
        return new Point(vector.x / length, vector.y / length);
    }

    static getScalarLengthAlong(deltaVector: paper.Point, basisVector: paper.Point): number {
        if (MathFunctions.isZeroVector(deltaVector) || MathFunctions.isZeroVector(basisVector)) {
            return 0;
        }
        deltaVector = deltaVector.project(basisVector);
        var unsignedLength = MathFunctions.getVectorLength(deltaVector);
        if (MathFunctions.isOppositeSign(deltaVector.x, basisVector.x) || MathFunctions.isOppositeSign(deltaVector.y, basisVector.y)) {
            return -1 * unsignedLength;
        }
        return unsignedLength;
    }

    static getScalarLengthAlongMatchingLinearVector(deltaVector: any, basisVector: any): number {
        if (MathFunctions.isZeroVector(deltaVector) || MathFunctions.isZeroVector(basisVector)) {
            return 0;
        }
        var unsignedLength = MathFunctions.getVectorLength(deltaVector);
        if (MathFunctions.isOppositeSign(deltaVector.x, basisVector.x) || MathFunctions.isOppositeSign(deltaVector.y, basisVector.y)) {
            return -1 * unsignedLength;
        }
        return unsignedLength;
    }

    static getVectorLength(vector: paper.Point): number {
        return Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));
    }

    static isOppositeSign(a: number, b: number): boolean {
        return ((a > 0 && b < 0) || (a < 0 && b > 0));
    }

    static pointsAreEqual(p1: paper.Point, p2: paper.Point): boolean {
        return (p1.x === p2.x && p1.y === p2.y);
    }

    static transposePoint(p: paper.Point): paper.Point {
        return new Point(p.y, p.x);
    }

    static getVectorBetween(p1: paper.Point, p2: paper.Point): paper.Point {
        return new Point(p2.x - p1.x, p2.y - p1.y);
    }

    static reverseVector(v: paper.Point): paper.Point {
        if (!v) {
            return new Point(0, 0);
        }
        return new Point(-v.x, -v.y);
    }

    static getScalarSlope(p1: paper.Point, p2?: paper.Point): (number | null) {
        if (p2) {
            return MathFunctions.getScalarSlope(MathFunctions.getVectorBetween(p1, p2));
        }
        if (p1.x === 0) {
            return null;
        }
        return p1.y / p1.x;
    }

    static isZeroVector(v: paper.Point): boolean {
        return ((v.x === 0) && (v.y === 0));
    }

    static linesIntersect(line1: SimpleLine, line2: SimpleLine): boolean {
        var intPoint = MathFunctions.getInterceptPoint(
            MathFunctions.getMidpoint(line1.point1, line1.point2),
            MathFunctions.getVectorBetween(line1.point1, line1.point2),
            line2.point2,
            MathFunctions.getVectorBetween(line2.point1, line2.point2));
        if (intPoint != null && isFinite(intPoint.x) && isFinite(intPoint.y)) {
            return MathFunctions.lineContainsLinearPoint(line1, intPoint) && MathFunctions.lineContainsLinearPoint(line2, intPoint);
        }
        return false;
    }

    static lineContainsPoint(line: SimpleLine, point: paper.Point, dontRound: boolean, projectLine: boolean): boolean {
        var slopeVector = this.getVectorBetween(line.point1, line.point2);
        if (!dontRound) {
            slopeVector = this.roundVector(slopeVector);
        }
        if (slopeVector.x === 0) {
            return (point.x === line.point1.x) && MathFunctions.numberIsBetween(point.y, line.point1.y, line.point2.y);
        }
        var slope = MathFunctions.getScalarSlope(line.point1, line.point2);
        if (slope == null) {
            return false;
        }
        var yInt = MathFunctions.solveForYIntercept(slope, line.point1);
        var yAtX = slope * point.x + yInt;//y = mx+b
        var pointY = point.y;
        if (!dontRound) {
            pointY = Math.round(pointY);
            yAtX = Math.round(yAtX);
        }

        if (!(pointY === yAtX)) {
            return false;
        }
        if (projectLine) {
            return true;
        }
        return MathFunctions.lineContainsLinearPoint(line, point, dontRound);
    }

    static lineContainsLinearPoint(line: SimpleLine, point: paper.Point, dontRound?: boolean): boolean {
        var vectorShift = MathFunctions.reverseVector(line.point1);
        var lineVector = MathFunctions.addVectors(line.point2, vectorShift);
        var newLinearPoint = MathFunctions.addVectors(point, vectorShift);
        if (!dontRound) {
            lineVector = MathFunctions.roundVector(lineVector);
            newLinearPoint = MathFunctions.roundVector(newLinearPoint);
        }
        var lineEndsAt = MathFunctions.getVectorLength(lineVector); //0 to that.
        var pointIsAt = MathFunctions.getScalarLengthAlongMatchingLinearVector(newLinearPoint, lineVector);
        if (!dontRound) {
            lineEndsAt = Math.round(lineEndsAt);
            pointIsAt = Math.round(pointIsAt);
        }
        return MathFunctions.numberIsBetween(pointIsAt, 0, lineEndsAt);
    }

    static numberIsBetween(num: number, a: number, b: number): boolean {
        if (a > b) {
            return MathFunctions.numberIsBetween(num, b, a);
        }
        //a <= b:
        return (num >= a) && (num <= b);
    }

    static getInterceptPoint(startingPoint: paper.Point, slopeVector: paper.Point, interceptLinePoint: paper.Point, interceptLineSlopeVector: paper.Point, dontRoundVectors?: boolean): any {//projects intercept line to get an answer.
        if (!dontRoundVectors) {
            return this.getInterceptPoint(MathFunctions.roundVector(startingPoint), MathFunctions.roundVector(slopeVector.normalize(100)), MathFunctions.roundVector(interceptLinePoint), MathFunctions.roundVector(interceptLineSlopeVector.normalize(100)), true);
        }
        if (this.pointsAreEqual(startingPoint, interceptLinePoint)) {
            return startingPoint;
        }

        if (this.pointsAreEqual(slopeVector, interceptLineSlopeVector) || (slopeVector.x === 0 && interceptLineSlopeVector.x === 0)) {
            return null;
        }
        if (this.pointsAreEqual(new Point(0, 0), interceptLineSlopeVector)) {
            return MathFunctions.lineContainsPoint({
                point1: startingPoint,
                point2: MathFunctions.addVectors(startingPoint, slopeVector)
            }, interceptLinePoint, true, true);
        }
        if (this.pointsAreEqual(new Point(0, 0), slopeVector)) {
            return MathFunctions.lineContainsPoint({
                point1: interceptLinePoint,
                point2: MathFunctions.addVectors(interceptLinePoint, interceptLineSlopeVector)
            }, startingPoint, true, true);
        }
        if (slopeVector.x === 0) {
            return MathFunctions.transposePoint(MathFunctions.getInterceptPoint(MathFunctions.transposePoint(startingPoint), MathFunctions.transposePoint(slopeVector), MathFunctions.transposePoint(interceptLinePoint), MathFunctions.transposePoint(interceptLineSlopeVector)));
        }
        var slope = MathFunctions.getScalarSlope(slopeVector);
        if (slope == null) {
            return null;
        }
        if (interceptLineSlopeVector.x === 0) {
            var yValue = MathFunctions.solveForYIntercept(slope, new Point(startingPoint.x - interceptLinePoint.x, startingPoint.y));
            return new Point(interceptLinePoint.x, yValue);
        }
        var interceptLineSlope = MathFunctions.getScalarSlope(interceptLineSlopeVector);
        if (slope === interceptLineSlope || interceptLineSlope == null) {
            return null;
        }
        var startingPointYIntercept = MathFunctions.solveForYIntercept(slope, startingPoint);
        var interceptLineYInterccept = MathFunctions.solveForYIntercept(interceptLineSlope, interceptLinePoint);

        //solve for shared x (allowed to set equal bc they share equal y-value at x):
        //m1x+b1 = m2x+b2
        //(m1-m2)x = (b2-b1)
        //x = (b2-b1)/(m1-m2);
        //find either y: = m1*x+b1

        var xVal = (interceptLineYInterccept - startingPointYIntercept) / (slope - interceptLineSlope);
        var yVal = slope * xVal + startingPointYIntercept;

        return new Point(xVal, yVal);
    }

    static solveForYIntercept(slope: number, point: paper.Point): number {
        var pointYIntercept: number;
        //y=mx+b
        //(y at x==0)=slope*0+b
        //..=b
        //p.y=slope*p.x+b;
        //b=p.y-(slope*p.x)
        if (point.x === 0 || slope === 0) {
            pointYIntercept = point.y;
        } else {
            pointYIntercept = point.y - (slope * point.x);
        }
        return pointYIntercept;
    }

    static calculateRadiusFromArc(arcWidth: number, arcDepth: number): number {
        var halfLength = arcWidth / 2;
        var radius = ((arcDepth * arcDepth) + (halfLength * halfLength)) / (2 * arcDepth);
        return radius;
    }

    static calculateArcDepth(arcWidth: number, radius: number): number {
        var halfLength = arcWidth / 2;
        var depthRadical = Math.sqrt((radius * radius) - (halfLength * halfLength));
        return (radius - depthRadical);
    }

    static calculateArcLength(arcWidth: number, arcDepth: number): number {
        var halfLength = arcWidth / 2;
        var radius = ((arcDepth * arcDepth) + (halfLength * halfLength)) / (2 * arcDepth);
        var angleInRadians = 2 * ((Math.PI / 2) - (Math.acos(halfLength / radius)));
        return angleInRadians * radius;
    }

    static getLineLength(line: SimpleLine): number {
        return MathFunctions.getDistanceBetween(line.point1, line.point2);
    }

    static getLineVector(line: SimpleLine): paper.Point {
        return MathFunctions.getVectorBetweenPoints(line.point1, line.point2);
    }

    static getInterceptBetweenSimpleLines(line1: SimpleLine, line2: SimpleLine, dontRound: boolean = false) {
        return MathFunctions.getInterceptPoint(line1.point1, MathFunctions.getLineVector(line1), line2.point1, MathFunctions.getLineVector(line2), dontRound);
    }

    static getLineNormal(line: SimpleLine): paper.Point {
        return MathFunctions.getUnitNormal(line.point1, line.point2);
    }

    static getLineMidpoint(line: SimpleLine) {
        return MathFunctions.getMidpoint(line.point1, line.point2);
    }

    static isHorizontalLine(line: SimpleLine, round: boolean): boolean {
        return MathFunctions.isHorizontallyAligned(line.point1, line.point2, round);
    }

    static isVerticalLine(line: SimpleLine, round: boolean): boolean {
        return this.isVerticallyAligned(line.point1, line.point2, round);
    }

    static calculateAngleFromLines(lineIn: SimpleLine, lineOut: SimpleLine): number {
        var curve1Point = lineIn.point1;
        var angleVertexPoint = lineIn.point2;
        var curve2Point = lineOut.point2;

        var v1 = MathFunctions.reverseVector(MathFunctions.getVectorBetweenPoints(curve1Point, angleVertexPoint)).normalize();
        var v2 = MathFunctions.getVectorBetweenPoints(angleVertexPoint, curve2Point).normalize();
        return v1.getAngle(v2);
    }

    static calculateOutVectorFromAngle(vectorIn: paper.Point, angle: number, counterClockwise: boolean): paper.Point {
        if (counterClockwise) {
            angle = -1 * angle;
        }
        var v = vectorIn.rotate(angle, new Point(0, 0));
        return v;
    }

    static convertToRadians(angle: number): number {
        return (angle / 360) * 2 * Math.PI;
    }

    static closestToZero(a: number, b: number): number {
        var absA = Math.abs(a);
        var absB = Math.abs(b);
        if (absB < absA) {
            return b;
        }
        return a;
    }

    static getLineBetween(p1: paper.Point, p2: paper.Point): SimpleLine {
        return new SimpleLine(p1, p2);
    }
}
